import {
  addManualItem,
  addNote,
  dictate,
  giveConsent,
  OfflineError,
  uploadPhoto,
  uploadRecording,
  upsertSession,
  type DictationResultDto,
} from './field-intake-api.js';
import type { DictationBody } from '@reos/field-intake';
import { t } from '@reos/i18n';
import { DomainApiError } from './api-error.js';

/**
 * Krok UXV4c bod 3 — OFFLINE FRONTA naboru.
 *
 * Na schuzce u majitele casto neni signal. Vse, co makler udela (zalozeni
 * relace, souhlas, nahravka, fotka, poznamka), jde NEJDRIV do fronty
 * v IndexedDB a odtud na server, jakmile je sit. Server je na `id`
 * idempotentni, takze opakovane odeslani (vypadek v pulce) nic nezdvoji.
 *
 * Poradi je zachovane (FIFO): souhlas musi dorazit pred nahravkou rozhovoru,
 * jinak by ji server odmitl. Pri vypadku site se odesilani zastavi a zbytek
 * pocka na udalost `online`.
 */

export type QueueOp =
  | { readonly kind: 'session'; readonly sessionId: string; readonly body: Parameters<typeof upsertSession>[1] }
  | { readonly kind: 'consent'; readonly sessionId: string }
  | {
      readonly kind: 'recording';
      readonly sessionId: string;
      readonly id: string;
      readonly blob: Blob;
      readonly recordingKind: 'conversation' | 'self_dictation';
      readonly durationS: number;
    }
  /*
   * Krok NAB3: fotka muze patrit MISTNOSTI (zalozka „Místnosti"). Urceni jde
   * frontou s fotkou, takze offline nabor o mistnost neprijde.
   */
  | {
      readonly kind: 'photo';
      readonly sessionId: string;
      readonly id: string;
      readonly blob: Blob;
      readonly atSeconds: number;
      readonly roomId?: string | undefined;
      readonly roomType?: string | undefined;
    }
  | { readonly kind: 'note'; readonly sessionId: string; readonly id: string; readonly atSeconds: number; readonly text: string }
  /**
   * Krok NAB1 — hodnota bodu, ktery makler odskrtl rucne. Jde do relace
   * STEJNOU frontou jako poznamka, takze se v rozboru objevi jako navrh
   * s puvodem „ručně" a do zakazky se propise jako udaj z nahravky.
   */
  | {
      readonly kind: 'manualFact';
      readonly sessionId: string;
      readonly factKey: string;
      readonly value: unknown;
      readonly atSeconds: number;
      /**
       * Krok NAB2 — PUVOD HODNOTY bodu checklistu („makléř viděl" /
       * „majitel tvrdí"). Jde do citace polozky, takze stoji v provenienci
       * faktu (`planFactWrite` ji dava do `notes`) a makler pozdeji pozna,
       * odkud se hodnota vzala.
       */
      readonly note?: string | undefined;
    }
  /**
   * Krok NAB4 — ROZBOR DIKTATU ke skupine nebo mistnosti. Stoji ve fronte ZA
   * nahravkou diktatu (FIFO), takze bez signálu se diktat ulozi a rozebere az
   * po pripojeni — presne jako zadani rika. Vysledek jsou navrhy; obrazovka je
   * dostane pres `onResult`, protoze fronta muze odeslat o hodinu pozdeji.
   */
  | {
      readonly kind: 'dictation';
      readonly sessionId: string;
      /** Klic cile na obrazovce (`group:<id>` / `room:<id>`) — kam navrhy patri. */
      readonly scopeKey: string;
      readonly body: DictationBody;
    };

/**
 * Krok P8F2 — ODLOZENA POLOZKA. Server rekl, ze obsah polozky prijmout nemuze
 * (422): opakovani ji nikdy neprotlaci, takze se polozka odlozi (ZUSTAVA ve
 * fronte, nic se tise nezahodi) a zbytek fronty jde dal. Ceka, dokud ji makler
 * neopravi (nova odpoved tehoz bodu) nebo nesmaze.
 */
export interface QueueRejection {
  readonly seq: number;
  readonly kind: QueueOp['kind'];
  /** Pole, u ktereho odmitnuti vzniklo (`null` = polozka bez faktu). */
  readonly factKey: string | null;
  /** Duvod od serveru (problem+json `detail`/`title`) — uz lokalizovany. */
  readonly reason: string;
}

export interface QueueEntry {
  readonly seq: number;
  readonly op: QueueOp;
  /** `undefined`/`null` = polozka ceka na odeslani; jinak odlozena (viz vyse). */
  readonly rejected?: Pick<QueueRejection, 'factKey' | 'reason'> | null | undefined;
}

/** Uloziste fronty — IndexedDB v prohlizeci, pamet v testech. */
export interface QueueStore {
  add(op: QueueOp): Promise<void>;
  list(): Promise<QueueEntry[]>;
  remove(seq: number): Promise<void>;
  /** Odlozi polozku (prezije obnoveni stranky, proto jde do uloziste). */
  reject(seq: number, rejection: Pick<QueueRejection, 'factKey' | 'reason'>): Promise<void>;
}

export class MemoryQueueStore implements QueueStore {
  private seq = 0;
  private entries: QueueEntry[] = [];
  async add(op: QueueOp) {
    this.seq += 1;
    this.entries.push({ seq: this.seq, op });
  }
  async list() {
    return [...this.entries];
  }
  async remove(seq: number) {
    this.entries = this.entries.filter((e) => e.seq !== seq);
  }
  async reject(seq: number, rejection: Pick<QueueRejection, 'factKey' | 'reason'>) {
    this.entries = this.entries.map((e) => (e.seq === seq ? { ...e, rejected: rejection } : e));
  }
}

const DB_NAME = 'reos-field-intake';
const STORE = 'queue';

/** IndexedDB (Blob se tam uklada nativne — nahravka prezije zavreni prohlizece). */
export class IndexedDbQueueStore implements QueueStore {
  private dbPromise: Promise<IDBDatabase> | null = null;

  private db(): Promise<IDBDatabase> {
    this.dbPromise ??= new Promise((resolve, reject) => {
      const open = indexedDB.open(DB_NAME, 1);
      open.onupgradeneeded = () => {
        open.result.createObjectStore(STORE, { keyPath: 'seq', autoIncrement: true });
      };
      open.onsuccess = () => resolve(open.result);
      open.onerror = () => reject(open.error ?? new Error('indexeddb'));
    });
    return this.dbPromise;
  }

  private async tx<T>(mode: IDBTransactionMode, work: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
    const db = await this.db();
    return new Promise((resolve, reject) => {
      const request = work(db.transaction(STORE, mode).objectStore(STORE));
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error ?? new Error('indexeddb'));
    });
  }

  async add(op: QueueOp) {
    await this.tx('readwrite', (s) => s.add({ op }));
  }
  async list() {
    const rows = await this.tx('readonly', (s) => s.getAll() as IDBRequest<QueueEntry[]>);
    return [...rows].sort((a, b) => a.seq - b.seq);
  }
  async remove(seq: number) {
    await this.tx('readwrite', (s) => s.delete(seq));
  }
  async reject(seq: number, rejection: Pick<QueueRejection, 'factKey' | 'reason'>) {
    const row = await this.tx('readonly', (s) => s.get(seq) as IDBRequest<QueueEntry | undefined>);
    if (row === undefined) return;
    await this.tx('readwrite', (s) => s.put({ ...row, rejected: rejection }));
  }
}

export type Sender = (op: QueueOp) => Promise<unknown>;

/** Skutecne odeslani na API. */
export const sendToApi: Sender = (op) => {
  switch (op.kind) {
    case 'session':
      return upsertSession(op.sessionId, op.body);
    case 'consent':
      return giveConsent(op.sessionId);
    case 'recording':
      return uploadRecording(op.sessionId, op.id, op.blob, op.recordingKind, op.durationS);
    case 'photo':
      return uploadPhoto(op.sessionId, op.id, op.blob, op.atSeconds, {
        ...(op.roomId === undefined ? {} : { roomId: op.roomId }),
        ...(op.roomType === undefined ? {} : { roomType: op.roomType }),
      });
    case 'note':
      return addNote(op.sessionId, op.id, op.atSeconds, op.text);
    case 'manualFact':
      return addManualItem(op.sessionId, { factKey: op.factKey, value: op.value, quote: op.note ?? null, atSeconds: op.atSeconds });
    case 'dictation':
      return dictate(op.sessionId, op.body);
  }
};

export interface FlushResult {
  readonly sent: number;
  /** Kolik polozek ceka na odeslani — BEZ odlozenych (ty uz na sit necekaji). */
  readonly pending: number;
  /** Server polozku odmitl (ne sit) — zustava ve fronte, UI ukaze hlasku. */
  readonly rejected: Error | null;
  /** Krok P8F2 — odlozene polozky (422). Zbytek fronty odesel. */
  readonly rejections: readonly QueueRejection[];
}

/** Polozka, u ktere odmitnuti vzniklo, nese klic faktu (`null` u ostatnich). */
function factKeyOf(op: QueueOp): string | null {
  return op.kind === 'manualFact' ? op.factKey : null;
}

function rejectionsOf(entries: readonly QueueEntry[]): readonly QueueRejection[] {
  return entries.flatMap((entry) =>
    entry.rejected === undefined || entry.rejected === null
      ? []
      : [{ seq: entry.seq, kind: entry.op.kind, factKey: entry.rejected.factKey, reason: entry.rejected.reason }],
  );
}

export class OfflineQueue {
  private flushing: Promise<FlushResult> | null = null;
  private readonly listeners = new Set<(pending: number) => void>();
  private readonly dictationListeners = new Set<(scopeKey: string, result: DictationResultDto) => void>();

  constructor(
    private readonly store: QueueStore,
    private readonly send: Sender = sendToApi,
    private readonly isOnline: () => boolean = () => (typeof navigator === 'undefined' ? true : navigator.onLine),
  ) {}

  onChange(listener: (pending: number) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Krok NAB4 — navrhy z rozboru diktatu. Vysledek nejde vratit z `enqueue`:
   * bez signálu odesle frontu az udalost `online`, tedy klidne o hodinu
   * pozdeji. Obrazovka proto ceka tady.
   */
  onDictation(listener: (scopeKey: string, result: DictationResultDto) => void): () => void {
    this.dictationListeners.add(listener);
    return () => this.dictationListeners.delete(listener);
  }

  private async notify(): Promise<void> {
    const pending = await this.pending();
    for (const l of this.listeners) l(pending);
  }

  /** Kolik polozek ceka na odeslani. Odlozene (422) se nepocitaji. */
  async pending(): Promise<number> {
    return (await this.store.list()).filter((entry) => entry.rejected === undefined || entry.rejected === null).length;
  }

  /** Krok P8F2 — odlozene polozky, ktere ceka na opravu od maklere. */
  async rejections(): Promise<readonly QueueRejection[]> {
    return rejectionsOf(await this.store.list());
  }

  /** Makler odlozenou polozku smazal — zahodi se (uz nic neblokuje). */
  async dropRejection(seq: number): Promise<void> {
    await this.store.remove(seq);
    await this.notify();
  }

  /** Zaradi a hned zkusi odeslat (kdyz je sit). */
  async enqueue(op: QueueOp): Promise<FlushResult> {
    /*
     * Krok P8F2 — OPRAVA ODLOZENE POLOZKY. Nova odpoved tehoz bodu tu starou
     * odmitnutou nahrazuje; bez toho by odmitnuta hodnota blokovala potvrzeni
     * naboru i po oprave a makler by ji musel jeste rucne mazat.
     */
    const factKey = factKeyOf(op);
    if (factKey !== null) {
      for (const rejection of await this.rejections()) {
        if (rejection.factKey === factKey) await this.store.remove(rejection.seq);
      }
    }
    await this.store.add(op);
    await this.notify();
    return this.flush();
  }

  /** Odesle frontu v poradi; pri vypadku site skonci a zbytek pocka. */
  flush(): Promise<FlushResult> {
    this.flushing ??= this.doFlush().finally(() => {
      this.flushing = null;
    });
    return this.flushing;
  }

  private async doFlush(): Promise<FlushResult> {
    let sent = 0;
    let rejected: Error | null = null;
    if (this.isOnline()) {
      for (const entry of await this.store.list()) {
        // Odlozena polozka (422) ceka na maklere — jinak by znovu zastavila frontu.
        if (entry.rejected !== undefined && entry.rejected !== null) continue;
        let result: unknown;
        try {
          result = await this.send(entry.op);
        } catch (error) {
          if (error instanceof OfflineError) break; // sit: zkusi se znovu po `online`
          const failure = error instanceof Error ? error : new Error(String(error));
          const status = failure instanceof DomainApiError ? failure.status : 0;
          /*
           * Krok P8F2 — NEPLATNY OBSAH (422). Opakovani ho nikdy neprotlaci,
           * takze se polozka ODLOZI a zbytek fronty odejde. Do kroku P8F2 tu
           * bylo `break`: vadna polozka zustala prvni navzdy, nabor hlasil
           * „Bez signálu" a makler uz neodeslal ani fotky.
           */
          if (status === 422 || status === 400) {
            await this.store.reject(entry.seq, { factKey: factKeyOf(entry.op), reason: failure.message });
            rejected ??= failure;
            continue;
          }
          /*
           * Krok P8F2 — PRIHLASENI A OPRAVNENI. Fronta STOJI (nic se nezahodi)
           * a hlaska rika, co ma makler udelat; technicky text serveru
           * („Unauthorized") mu nepomuze.
           */
          if (status === 401 || status === 403) {
            rejected = new Error(
              t(status === 401 ? 'webAdmin.fieldIntake.queue.signInAgain' : 'webAdmin.fieldIntake.queue.noAccess', {
                locale: 'cs',
              }),
            );
            break;
          }
          rejected = failure;
          break;
        }
        await this.store.remove(entry.seq);
        sent += 1;
        if (entry.op.kind === 'dictation' && result !== undefined && result !== null) {
          for (const listener of this.dictationListeners) listener(entry.op.scopeKey, result as DictationResultDto);
        }
      }
    }
    await this.notify();
    const entries = await this.store.list();
    return {
      sent,
      pending: entries.filter((entry) => entry.rejected === undefined || entry.rejected === null).length,
      rejected,
      rejections: rejectionsOf(entries),
    };
  }

  /** Po obnoveni site (`online`) odesle, co ceka. Vraci odhlaseni. */
  attachOnlineListener(target: Pick<Window, 'addEventListener' | 'removeEventListener'> = window): () => void {
    const handler = (): void => {
      void this.flush();
    };
    target.addEventListener('online', handler);
    return () => target.removeEventListener('online', handler);
  }
}

let shared: OfflineQueue | null = null;

/** Jedna fronta na prohlizec (IndexedDB), sdilena obrazovkami naboru. */
export function intakeQueue(): OfflineQueue {
  shared ??= new OfflineQueue(typeof indexedDB === 'undefined' ? new MemoryQueueStore() : new IndexedDbQueueStore());
  return shared;
}
